import { Link } from "@remix-run/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  appendWorkspaceTypeToPropertyLink,
  getSpaceTypeChipLabelText,
  getSpaceTypeChipStyle,
  SPACE_TYPE_CHIP_BUTTON_CLASS,
  SPACE_TYPE_CHIP_CLASS,
  SPACE_TYPE_CHIP_GAP_PX,
  type SpaceTypeCount,
} from "~/lib/helpers/spaceTypeChipHelper";

export type ListingSpaceTypeChipsProps = {
  spaceTypeCounts?: SpaceTypeCount[];
  spaceTypeLabel?: string;
  propertyLink: string;
  onEngagement?: () => void;
  onExpandedChange?: (expanded: boolean) => void;
  /** Extra chip-row height vs collapsed single row; drives carousel shrink in card layouts. */
  onExpandHeightDeltaChange?: (deltaPx: number) => void;

  /** Show all space types without More/Less button on property page. */
  isPropertyPage?: boolean;
  isListCard?: boolean;
};

export default function ListingSpaceTypeChips({
  spaceTypeCounts,
  spaceTypeLabel,
  propertyLink,
  onEngagement,
  onExpandedChange,
  onExpandHeightDeltaChange,
  isPropertyPage = false,
  isListCard = false,
}: ListingSpaceTypeChipsProps) {
  const [showAllSpaceTypes, setShowAllSpaceTypes] = useState(false);
  const [visibleChipCount, setVisibleChipCount] = useState(2);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const chipsRowRef = useRef<HTMLDivElement>(null);
  const chipsMeasureRef = useRef<HTMLDivElement>(null);
  const collapsedRowHeightRef = useRef(0);

  const spaceTypes = (spaceTypeCounts ?? []).filter(
    (item) => item?.spaceType && (item.count == null || item.count > 0),
  );
  const fallbackSpaceTypes =
    spaceTypes.length === 0 && spaceTypeLabel
      ? [{ spaceType: spaceTypeLabel, count: 0 }]
      : spaceTypes;

  const spaceTypesMeasureKey = fallbackSpaceTypes
    .map((item) => `${item.spaceType}:${item.count}`)
    .join("|");
  const primarySpaceTypes = fallbackSpaceTypes.slice(0, visibleChipCount);
  const remainingSpaceTypes = fallbackSpaceTypes.slice(visibleChipCount);
  const hiddenSpaceTypeCount = remainingSpaceTypes.length;

  // Track scroll position to show/hide scroll arrows in List Card mode
  const checkScrollPosition = () => {
    const el = chipsRowRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(
      Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth
    );
  };

  useEffect(() => {
    const el = chipsRowRef.current;
    if (!el || !isListCard) return;

    checkScrollPosition();
    el.addEventListener("scroll", checkScrollPosition);

    const observer = new ResizeObserver(checkScrollPosition);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", checkScrollPosition);
      observer.disconnect();
    };
  }, [isListCard, fallbackSpaceTypes]);

  const handleScroll = (direction: "left" | "right") => {
    const el = chipsRowRef.current;
    if (!el) return;
    const scrollAmount = direction === "left" ? -140 : 140;
    el.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  useLayoutEffect(() => {
    const row = chipsRowRef.current;
    const measure = chipsMeasureRef.current;
    if (!row || !measure || fallbackSpaceTypes.length === 0 || isListCard) return;

    const recalculate = () => {
      const availableWidth = row.clientWidth;
      if (availableWidth <= 0) return;

      const chipEls = Array.from(
        measure.querySelectorAll<HTMLElement>("[data-chip-measure]"),
      );
      if (!chipEls.length) return;

      const chipWidths = chipEls.map((el) => el.offsetWidth);
      const totalWithoutMore =
        chipWidths.reduce((sum, width) => sum + width, 0) +
        SPACE_TYPE_CHIP_GAP_PX * Math.max(0, chipWidths.length - 1);

      if (totalWithoutMore <= availableWidth) {
        setVisibleChipCount(chipWidths.length);
        return;
      }

      let fitCount = 1;
      for (let count = chipWidths.length - 1; count >= 1; count -= 1) {
        const hiddenCount = chipWidths.length - count;
        const moreEl = measure.querySelector<HTMLElement>(
          `[data-more-measure="${hiddenCount}"]`,
        );
        const moreWidth = moreEl?.offsetWidth ?? 72;
        const chipsWidth =
          chipWidths
            .slice(0, count)
            .reduce((sum, width) => sum + width, 0) +
          SPACE_TYPE_CHIP_GAP_PX * Math.max(0, count - 1);

        const rowWidth =
          chipsWidth + SPACE_TYPE_CHIP_GAP_PX + moreWidth;

        if (rowWidth <= availableWidth) {
          fitCount = count;
          break;
        }
      }

      setVisibleChipCount((prev) => {
        const next = Math.max(1, fitCount);
        return prev === next ? prev : next;
      });
    };

    recalculate();
    const observer = new ResizeObserver(recalculate);
    observer.observe(row);
    return () => observer.disconnect();
  }, [spaceTypesMeasureKey, fallbackSpaceTypes.length, isListCard]);

  useLayoutEffect(() => {
    const row = chipsRowRef.current;
    if (!row) return;

    const reportExpandHeightDelta = () => {
      const height = row.offsetHeight;

      if (!showAllSpaceTypes) {
        collapsedRowHeightRef.current = height;
        onExpandHeightDeltaChange?.(0);
        return;
      }

      const baseline = collapsedRowHeightRef.current || height;

      onExpandHeightDeltaChange?.(
        Math.max(0, height - baseline),
      );
    };

    reportExpandHeightDelta();
    const observer = new ResizeObserver(reportExpandHeightDelta);
    observer.observe(row);
    return () => observer.disconnect();
  }, [
    showAllSpaceTypes,
    spaceTypesMeasureKey,
    visibleChipCount,
    onExpandHeightDeltaChange,
  ]);

  if (fallbackSpaceTypes.length === 0) return null;

  return (
    <div className="relative min-w-0 flex items-center group/chips w-full">
      {/* Hidden measurement container */}
      <div
        ref={chipsMeasureRef}
        aria-hidden="true"
        className="absolute left-0 top-0 -z-10 flex w-max items-center gap-1.5 whitespace-nowrap invisible pointer-events-none"
      >
        {fallbackSpaceTypes.map((item) => {
          const { bgClass, textClass, Icon } =
            getSpaceTypeChipStyle(item.spaceType);

          const chipLabel = getSpaceTypeChipLabelText(item);

          return (
            <span
              key={`measure-${item.spaceType}-${item.count}`}
              data-chip-measure
              className={`${SPACE_TYPE_CHIP_CLASS} ${bgClass} ${textClass}`}
            >
              <Icon size={13} className="flex-shrink-0" />
              <span>{chipLabel}</span>
            </span>
          );
        })}
        {fallbackSpaceTypes.map((_, index) => {
          const hiddenCount = fallbackSpaceTypes.length - index;
          if (hiddenCount <= 0) return null;
          return (
            <span
              key={`measure-more-${hiddenCount}`}
              data-more-measure={hiddenCount}
              className={`${SPACE_TYPE_CHIP_CLASS} bg-gray-100 text-gray-600`}
            >
              +{hiddenCount} more
            </span>
          );
        })}
      </div>

      {isListCard && canScrollLeft && (
        <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center justify-start pr-10 bg-gradient-to-r from-white via-white/90 to-transparent pointer-events-none">
          <button
            type="button"
            aria-label="Scroll left"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleScroll("left");
            }}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 text-gray-700 hover:bg-gray-50 shrink-0 pointer-events-auto"
          >
            <ChevronLeft size={14} />
          </button>
        </div>
      )}

      {/* Chips Container */}
      <div
        ref={chipsRowRef}
        className={
          isListCard
            ? "flex items-center min-w-0 w-full flex-nowrap gap-1.5 overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            : `flex items-center min-w-0 w-full ${isPropertyPage || showAllSpaceTypes
              ? "flex-wrap gap-1"
              : "flex-nowrap gap-1.5"
            }`
        }
      >
        {(isPropertyPage || showAllSpaceTypes || isListCard
          ? fallbackSpaceTypes
          : primarySpaceTypes
        ).map((item) => {
          const { bgClass, textClass, Icon } =
            getSpaceTypeChipStyle(item.spaceType);

          const chipLabel = getSpaceTypeChipLabelText(item);

          const chipUrl = appendWorkspaceTypeToPropertyLink(
            propertyLink,
            item.spaceType,
          );

          return (
            <button
              key={`${item.spaceType}-${item.count}`}
              type="button"
              className={`${SPACE_TYPE_CHIP_BUTTON_CLASS} ${bgClass} ${textClass} shrink-0`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEngagement?.();
                if (chipUrl) {
                  window.open(chipUrl, "_blank");
                }
              }}
            >
              <Icon size={13} className="flex-shrink-0" />
              <span>{chipLabel}</span>
            </button>
          );
        })}

        {/* More / Less button only on non-property pages and non-listCard mode */}
        {!isPropertyPage && !isListCard && hiddenSpaceTypeCount > 0 ? (
          <button
            type="button"
            className={`${SPACE_TYPE_CHIP_CLASS} bg-gray-100 text-gray-600 hover:bg-gray-200 relative z-[11] shrink-0`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowAllSpaceTypes((prev) => {
                const next = !prev;
                onExpandedChange?.(next);
                return next;
              });
            }}
          >
            {showAllSpaceTypes
              ? "Show less"
              : `+${hiddenSpaceTypeCount} more`}
          </button>
        ) : null}
      </div>

      {/* Next Scroll Button */}
      {isListCard && canScrollRight && (
        <div className="absolute right-0 top-0 bottom-0 z-20 flex items-center justify-end pl-10 bg-gradient-to-l from-white via-white/90 to-transparent pointer-events-none">
          <button
            type="button"
            aria-label="Scroll right"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleScroll("right");
            }}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 text-gray-700 hover:bg-gray-50 shrink-0 pointer-events-auto"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}